import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  CalendarDays,
  CheckCircle2,
  Clock3,
  Download,
  ExternalLink,
  Image as ImageIcon,
  Loader2,
  MapPin,
  RefreshCw,
  Search,
  UserCheck,
  UserX,
} from "lucide-react";
import { http, apiError } from "@/lib/api";
import AdminShell, { PageHead, useAdmin } from "@/components/AdminShell";

function isoToday() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function isoMonthStart() {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  return `${y}-${m}-01`;
}

function durationText(minutes) {
  const value = Number(minutes || 0);
  if (!value) return "-";
  return `${Math.floor(value / 60)}j ${value % 60}m`;
}

function locationLabel(loc) {
  if (!loc) return "-";
  if (loc.inside_radius === true) return "Dalam Area";
  if (loc.inside_radius === false) return "Di Luar Area";
  return "Belum dihitung";
}

function mapUrl(loc) {
  if (loc?.latitude == null || loc?.longitude == null) return "";
  return `https://www.google.com/maps?q=${loc.latitude},${loc.longitude}`;
}

function attendanceTimestamp(row) {
  const raw =
    row?.check_in_at ||
    row?.check_out_at ||
    row?.created_at ||
    row?.date ||
    "";

  const time = new Date(raw).getTime();
  return Number.isFinite(time) ? time : 0;
}

function MediaButton({ href, title, icon: Icon, disabled = false }) {
  if (!href || disabled) {
    return (
      <span
        className="grid h-9 w-9 cursor-not-allowed place-items-center rounded-xl bg-card/50 text-muted-foreground/40"
        title={`${title} belum tersedia`}
      >
        <Icon size={14} />
      </span>
    );
  }

  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer"
      className="grid h-9 w-9 place-items-center rounded-xl bg-card text-primary transition hover:bg-primary/10"
      title={title}
    >
      <Icon size={14} />
    </a>
  );
}

function AttendanceAdminInner() {
  const admin = useAdmin();
  const isOwner = admin.user?.role === "owner";
  const [today, setToday] = useState(null);
  const [report, setReport] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [start, setStart] = useState(isoMonthStart());
  const [end, setEnd] = useState(isoToday());
  const [employeeId, setEmployeeId] = useState("");
  const [q, setQ] = useState("");

  async function loadToday() {
    const { data } = await http.get("/admin/attendance/owner/today");
    setToday(data);
  }

  async function loadEmployees() {
    const { data } = await http.get("/admin/users");
    setEmployees((data || []).filter((u) => u.role !== "owner"));
  }

  async function loadReport() {
    const params = new URLSearchParams();
    if (start) params.set("start", start);
    if (end) params.set("end", end);
    if (employeeId) params.set("employee_id", employeeId);

    const { data } = await http.get(
      `/admin/attendance/owner/report?${params.toString()}`
    );

    setReport(data || []);
  }

  async function loadAll() {
    if (!isOwner) {
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      await Promise.all([
        loadToday(),
        loadEmployees(),
        loadReport(),
      ]);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
  }, [isOwner]);

  useEffect(() => {
    if (!isOwner) return;

    loadReport().catch((e) => toast.error(apiError(e)));
  }, [start, end, employeeId]);

  const filteredReport = useMemo(() => {
    const needle = q.trim().toLowerCase();

    const rows = needle
      ? report.filter((row) =>
          `${row.employee_name || ""} ${row.employee_email || ""} ${
            row.date || ""
          }`
            .toLowerCase()
            .includes(needle)
        )
      : [...report];

    return rows.sort(
      (a, b) => attendanceTimestamp(b) - attendanceTimestamp(a)
    );
  }, [report, q]);

  const recentReport = useMemo(
    () => filteredReport.slice(0, 7),
    [filteredReport]
  );

  async function downloadExcel() {
    setExporting(true);

    try {
      const params = new URLSearchParams();
      if (start) params.set("start", start);
      if (end) params.set("end", end);
      if (employeeId) params.set("employee_id", employeeId);

      const response = await http.get(
        `/admin/attendance/owner/export?${params.toString()}`,
        {
          responseType: "blob",
        }
      );

      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });

      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;
      link.download = `rekap-absensi-${start || "all"}-sd-${
        end || "all"
      }.xlsx`;

      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);

      toast.success("Excel absensi berhasil diunduh.");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setExporting(false);
    }
  }

  if (!isOwner) {
    return (
      <div className="rounded-3xl border border-dashed border-border p-12 text-center text-muted-foreground">
        Halaman khusus pemilik.
      </div>
    );
  }

  if (loading || !today) {
    return (
      <div className="flex justify-center pt-20">
        <Loader2 className="wm-spin text-muted-foreground" />
      </div>
    );
  }

  const summary = today.summary || {};

  return (
    <>
      <PageHead
        eyebrow="Pemilik"
        title="Absensi Karyawan"
        desc="Pantau jam masuk/pulang, selfie, koordinat, keterlambatan, dan durasi kerja."
      >
        <button
          onClick={loadAll}
          className="flex items-center gap-2 rounded-xl bg-muted px-4 py-2.5 text-sm font-bold"
        >
          <RefreshCw size={16} />
          Refresh
        </button>
      </PageHead>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {[
          ["Karyawan", summary.employees || 0, CalendarDays],
          ["Sudah Masuk", summary.checked_in || 0, UserCheck],
          ["Belum Masuk", summary.not_checked_in || 0, UserX],
          ["Sudah Pulang", summary.checked_out || 0, CheckCircle2],
          ["Terlambat", summary.late || 0, Clock3],
        ].map(([label, value, Icon]) => (
          <div
            key={label}
            className="rounded-3xl border border-border bg-card p-4"
          >
            <Icon size={17} className="text-primary" />
            <div className="mt-2 font-heading text-2xl font-extrabold">
              {value}
            </div>
            <div className="text-xs text-muted-foreground">{label}</div>
          </div>
        ))}
      </div>

      <div className="mt-4 rounded-3xl border border-border bg-card p-5">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="font-heading text-lg font-extrabold">
              Hari Ini — {today.date}
            </h2>
            <div className="text-xs text-muted-foreground">
              Server: {today.server_time}
            </div>
          </div>
        </div>

        <div className="mt-4 grid gap-3 lg:grid-cols-2">
          {(today.items || []).map(({ employee, attendance }) => {
            const inLoc = attendance?.check_in_location;
            const outLoc = attendance?.check_out_location;

            return (
              <div
                key={employee.id}
                className="rounded-2xl bg-muted p-4"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <b className="font-heading text-base">
                      {employee.name}
                    </b>
                    <div className="text-xs text-muted-foreground">
                      {employee.email}
                    </div>
                  </div>

                  {!attendance?.check_in_at ? (
                    <span className="rounded-full bg-destructive/10 px-2 py-1 text-[10px] font-bold text-destructive">
                      Belum Masuk
                    </span>
                  ) : attendance?.late_minutes > 0 ? (
                    <span className="rounded-full bg-amber-500/15 px-2 py-1 text-[10px] font-bold text-amber-500">
                      Telat {attendance.late_minutes}m
                    </span>
                  ) : (
                    <span className="rounded-full bg-emerald-500/15 px-2 py-1 text-[10px] font-bold text-emerald-500">
                      Hadir
                    </span>
                  )}
                </div>

                <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                  <div className="rounded-xl bg-card p-3">
                    Masuk
                    <div className="mt-1 font-bold">
                      {attendance?.check_in_time || "-"}
                    </div>
                  </div>

                  <div className="rounded-xl bg-card p-3">
                    Pulang
                    <div className="mt-1 font-bold">
                      {attendance?.check_out_time || "-"}
                    </div>
                  </div>
                </div>

                {attendance && (
                  <div className="mt-3 space-y-2 text-xs text-muted-foreground">
                    <div>
                      Durasi kerja:{" "}
                      <b className="text-card-foreground">
                        {durationText(attendance.work_minutes)}
                      </b>
                    </div>

                    {inLoc && (
                      <div className="flex flex-wrap items-center gap-2">
                        <MapPin size={13} className="text-primary" />
                        Masuk: {locationLabel(inLoc)} ·{" "}
                        {inLoc.distance_from_store != null
                          ? `${Math.round(inLoc.distance_from_store)} m`
                          : "jarak -"}{" "}
                        · akurasi ±{Math.round(inLoc.accuracy || 0)} m
                        {mapUrl(inLoc) && (
                          <a
                            href={mapUrl(inLoc)}
                            target="_blank"
                            rel="noreferrer"
                            className="font-bold text-primary"
                          >
                            Peta{" "}
                            <ExternalLink
                              size={11}
                              className="inline"
                            />
                          </a>
                        )}
                      </div>
                    )}

                    {outLoc && (
                      <div className="flex flex-wrap items-center gap-2">
                        <MapPin size={13} className="text-primary" />
                        Pulang: {locationLabel(outLoc)} ·{" "}
                        {outLoc.distance_from_store != null
                          ? `${Math.round(outLoc.distance_from_store)} m`
                          : "jarak -"}{" "}
                        · akurasi ±{Math.round(outLoc.accuracy || 0)} m
                        {mapUrl(outLoc) && (
                          <a
                            href={mapUrl(outLoc)}
                            target="_blank"
                            rel="noreferrer"
                            className="font-bold text-primary"
                          >
                            Peta{" "}
                            <ExternalLink
                              size={11}
                              className="inline"
                            />
                          </a>
                        )}
                      </div>
                    )}
                  </div>
                )}

                {(attendance?.check_in_photo ||
                  attendance?.check_out_photo) && (
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    {attendance?.check_in_photo && (
                      <a
                        href={attendance.check_in_photo}
                        target="_blank"
                        rel="noreferrer"
                        className="overflow-hidden rounded-xl border border-border bg-card"
                      >
                        <img
                          src={attendance.check_in_photo}
                          alt="Selfie masuk"
                          className="h-28 w-full object-cover"
                        />
                        <div className="p-2 text-center text-[10px] font-bold">
                          Foto Masuk
                        </div>
                      </a>
                    )}

                    {attendance?.check_out_photo && (
                      <a
                        href={attendance.check_out_photo}
                        target="_blank"
                        rel="noreferrer"
                        className="overflow-hidden rounded-xl border border-border bg-card"
                      >
                        <img
                          src={attendance.check_out_photo}
                          alt="Selfie pulang"
                          className="h-28 w-full object-cover"
                        />
                        <div className="p-2 text-center text-[10px] font-bold">
                          Foto Pulang
                        </div>
                      </a>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      <div className="mt-4 rounded-3xl border border-border bg-card p-5">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h2 className="font-heading text-lg font-extrabold">
              Riwayat & Rekap
            </h2>
            <div className="text-xs text-muted-foreground">
              Menampilkan 7 absensi terbaru sesuai filter.
            </div>
          </div>

          <button
            disabled={exporting}
            onClick={downloadExcel}
            className="flex items-center gap-2 rounded-xl bg-primary px-4 py-2.5 text-sm font-bold text-primary-foreground disabled:opacity-50"
          >
            {exporting ? (
              <Loader2 className="wm-spin" size={16} />
            ) : (
              <Download size={16} />
            )}
            {exporting ? "Membuat Excel..." : "Unduh Excel"}
          </button>
        </div>

        <div className="mt-4 grid gap-2 md:grid-cols-4">
          <label className="text-xs font-bold text-muted-foreground">
            Dari
            <input
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              className="mt-1 w-full rounded-xl border border-border bg-muted px-3 py-2.5 text-sm font-normal text-card-foreground outline-none"
            />
          </label>

          <label className="text-xs font-bold text-muted-foreground">
            Sampai
            <input
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              className="mt-1 w-full rounded-xl border border-border bg-muted px-3 py-2.5 text-sm font-normal text-card-foreground outline-none"
            />
          </label>

          <label className="text-xs font-bold text-muted-foreground">
            Karyawan
            <select
              value={employeeId}
              onChange={(e) => setEmployeeId(e.target.value)}
              className="mt-1 w-full rounded-xl border border-border bg-muted px-3 py-2.5 text-sm font-normal text-card-foreground outline-none"
            >
              <option value="">Semua Karyawan</option>
              {employees.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.name}
                </option>
              ))}
            </select>
          </label>

          <label className="text-xs font-bold text-muted-foreground">
            Cari
            <div className="mt-1 flex items-center rounded-xl border border-border bg-muted px-3">
              <Search size={14} />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Nama / email"
                className="w-full bg-transparent px-2 py-2.5 text-sm font-normal outline-none"
              />
            </div>
          </label>
        </div>

        <div className="mt-4 space-y-3">
          {recentReport.map((row) => {
            const inLoc = row.check_in_location || {};
            const outLoc = row.check_out_location || {};

            return (
              <div
                key={row.id}
                className="rounded-2xl bg-muted p-4"
              >
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <div className="flex flex-wrap items-center gap-2">
                      <b className="text-sm">{row.employee_name}</b>

                      {row.shift_name && (
                        <span className="rounded-full bg-card px-2 py-1 text-[10px] font-bold text-muted-foreground">
                          {row.shift_name}
                        </span>
                      )}
                    </div>

                    <div className="mt-1 text-xs text-muted-foreground">
                      {row.date} · Masuk {row.check_in_time || "-"} ·
                      Pulang {row.check_out_time || "-"}
                    </div>
                  </div>

                  <div className="flex flex-wrap items-center gap-2 text-xs">
                    <span className="rounded-full bg-card px-2 py-1">
                      {durationText(row.work_minutes)}
                    </span>

                    {row.late_minutes > 0 ? (
                      <span className="rounded-full bg-amber-500/15 px-2 py-1 font-bold text-amber-500">
                        Telat {row.late_minutes}m
                      </span>
                    ) : row.check_in_at ? (
                      <span className="rounded-full bg-emerald-500/15 px-2 py-1 font-bold text-emerald-500">
                        Tepat Waktu
                      </span>
                    ) : null}

                    {(inLoc.inside_radius === false ||
                      outLoc.inside_radius === false) && (
                      <span className="rounded-full bg-destructive/10 px-2 py-1 font-bold text-destructive">
                        Di Luar Area
                      </span>
                    )}
                  </div>
                </div>

                <div className="mt-3 grid gap-2 sm:grid-cols-2">
                  <div className="rounded-xl border border-border bg-card/60 p-3">
                    <div className="mb-2 text-[10px] font-black uppercase tracking-[0.12em] text-muted-foreground">
                      Check-in
                    </div>

                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 text-xs text-muted-foreground">
                        <div>
                          Jam{" "}
                          <b className="text-card-foreground">
                            {row.check_in_time || "-"}
                          </b>
                        </div>
                        <div className="mt-1 truncate">
                          {locationLabel(inLoc)}
                          {inLoc.distance_from_store != null
                            ? ` · ${Math.round(
                                inLoc.distance_from_store
                              )} m`
                            : ""}
                        </div>
                      </div>

                      <div className="flex flex-none gap-1.5">
                        <MediaButton
                          href={row.check_in_photo}
                          title="Foto Check-in"
                          icon={ImageIcon}
                        />
                        <MediaButton
                          href={mapUrl(inLoc)}
                          title="Peta Check-in"
                          icon={MapPin}
                        />
                      </div>
                    </div>
                  </div>

                  <div className="rounded-xl border border-border bg-card/60 p-3">
                    <div className="mb-2 text-[10px] font-black uppercase tracking-[0.12em] text-muted-foreground">
                      Check-out
                    </div>

                    <div className="flex items-center justify-between gap-3">
                      <div className="min-w-0 text-xs text-muted-foreground">
                        <div>
                          Jam{" "}
                          <b className="text-card-foreground">
                            {row.check_out_time || "-"}
                          </b>
                        </div>
                        <div className="mt-1 truncate">
                          {row.check_out_at
                            ? locationLabel(outLoc)
                            : "Belum absen pulang"}
                          {outLoc.distance_from_store != null
                            ? ` · ${Math.round(
                                outLoc.distance_from_store
                              )} m`
                            : ""}
                        </div>
                      </div>

                      <div className="flex flex-none gap-1.5">
                        <MediaButton
                          href={row.check_out_photo}
                          title="Foto Check-out"
                          icon={ImageIcon}
                        />
                        <MediaButton
                          href={mapUrl(outLoc)}
                          title="Peta Check-out"
                          icon={MapPin}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}

          {recentReport.length === 0 && (
            <div className="py-10 text-center text-sm text-muted-foreground">
              Tidak ada data absensi pada filter ini.
            </div>
          )}

          {filteredReport.length > 7 && (
            <div className="pt-1 text-center text-[11px] text-muted-foreground">
              Menampilkan 7 data terbaru dari {filteredReport.length} data
              pada filter ini. File Excel tetap berisi seluruh data sesuai
              filter.
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default function AttendanceAdmin() {
  return (
    <AdminShell>
      <AttendanceAdminInner />
    </AdminShell>
  );
}
